import * as React from 'react';
import { connect } from 'react-redux';
import { __ } from '@bart.sk-eshop/react-i18n';
import { Link } from 'react-router';
import { push } from 'react-router-redux';
import styled from 'styled-components';
import { rem } from 'polished';
import ReactSelectLib, { components } from 'react-select';
import AnimateHeight from 'react-animate-height';
import Skvelko from '../../components/_helpers/Icon/Skvelko';
import { ButtonStyles } from '../../components/_helpers/form/Button';
import { CaretStyles } from '../../components/_helpers/Icon/Caret';
import { normalizeString, prop } from '../../utilities';
import {
  loadSkvelkoSchoolsByCity,
  loadSkvelkoSetsBySchool,
  loadSkvelkoClassesBySchool,
  resetSkvelko,
  setCurrentSet,
  setCityIsSelected,
  setSchoolIsSelected,
  setSelectedCityName,
  setSelectedSchoolId,
  setSelectedSkvelkoSetId,
  setSelectedClassId,
  setSkvelkoSetsBySchools,
  setSkvelkoClassesBySchools,
} from '../Home/actions';

/* ------------------------------------------------------------------ *
 * Skvelko box ("Zoznamy pomôcok na nový školský rok") — the bright
 * yellow card with the city → škola → ročník finder, redirecting to
 * /sada/:id on set selection. Built for the "Pre seba" homepage
 * (SelfIntro §1, Figma) and shared with the Sets page search box,
 * where it replaced the legacy Homepage/Filter. Consumers must load
 * the skvelko cities (loadSkvelkoCities) themselves.
 * ------------------------------------------------------------------ */

const ASSET = '/images/self-intro';

const YELLOW = '#ffdd00';
const RED = '#e3173e';
const DARK = '#231f20';
const GREY = '#4d4d4d';

const SkvelkoCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  overflow: hidden;
  background: ${YELLOW};
  border-radius: ${rem(24)};
  /* Mobile: no padding — the intro adds its own gutters and the form card sits
     flush to the card's bottom/left/right edges. */
  padding: 0;
  ${({ theme }) => theme.media('md')} {
    flex-direction: row;
    align-items: stretch;
    gap: ${rem(24)};
    border-radius: ${rem(32)};
    padding: ${rem(28)};
  }
`;

const SkvelkoIntro = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  padding: ${rem(16)} ${rem(16)} ${rem(20)};
  ${({ theme }) => theme.media('md')} {
    flex: 3 1 0;
    min-width: 0;
    padding: ${rem(8)} 0 0;
  }
`;

const SkvelkoBrandRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${rem(12)} ${rem(12)};
  ${({ theme }) => theme.media('md')} {
    gap: ${rem(12)} ${rem(48)};
  }
`;

const SkvelkoLogoWrap = styled.div`
  flex: 0 0 auto;
  margin-left: ${rem(4)};
  svg {
    width: ${rem(92)};
    height: auto;
  }
  ${({ theme }) => theme.media('md')} {
    margin-left: ${rem(16)};
    svg {
      width: ${rem(190)};
    }
  }
`;

const SrOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

const SkvelkoHeading = styled.h1`
  margin: 0;
  flex: 1 1 0;
  font-family: ${({ theme }) => theme.fonts.headings};
  font-weight: 700;
  font-size: ${rem(14)};
  line-height: 1.25;
  color: #000;
  ${({ theme }) => theme.media(400)} {
    font-size: ${rem(18)};
  }
  ${({ theme }) => theme.media('md')} {
    flex: 0 1 auto;
    font-size: ${rem(28)};
    max-width: ${rem(320)};
  }
`;

const IntroToggle = styled.button`
  ${ButtonStyles};
  align-self: flex-start;
  font-size: ${rem(16)};
  font-weight: 600;
  padding: 0;
  ${({ theme }) => theme.media('md')} {
    display: none;
  }
`;

const Caret = styled.span`
  ${CaretStyles};
  margin-left: ${rem(10)};
`;

const IntroCollapse = styled(AnimateHeight)`
  margin-top: -${rem(16)};
  ${({ theme }) => theme.media('md')} {
    height: auto !important;
    overflow: visible !important;
    /* Grow to fill the intro column so the bottom row can bottom out. */
    flex: 1 1 auto;
    display: flex;
    & > div {
      display: flex !important;
      flex: 1 1 auto;
    }
  }
`;

const CollapseInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  padding-top: ${rem(16)};
  ${({ theme }) => theme.media('md')} {
    flex: 1 1 auto;
  }
`;

const SkvelkoSubheading = styled.p`
  margin: ${rem(8)} 0 0;
  font-size: ${rem(16)};
  font-weight: 700;
  line-height: 1.4;
  color: ${DARK};
  ${({ theme }) => theme.media('md')} {
    max-width: ${rem(560)};
    font-size: ${rem(20)};
  }
`;

const SkvelkoText = styled.p`
  margin: 0;
  font-size: ${rem(16)};
  font-weight: 600;
  line-height: 1.45;
  color: ${GREY};
  ${({ theme }) => theme.media('md')} {
    max-width: ${rem(580)};
    font-size: ${rem(18)};
  }
`;

const Bullets = styled.ul`
  list-style: none;
  margin: ${rem(20)} 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  ${({ theme }) => theme.media('md')} {
    /* 35% of the whole banner (left column is 60%) */
    flex: 35 1 0;
    min-width: 0;
    margin: 0;
  }
  /* Tablet / small desktop (≤1024px): even 30/30 split with the girl. */
  ${({ theme }) => theme.media('md', 1025)} {
    flex: 30 1 0;
  }
`;

const IntroBottom = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  ${({ theme }) => theme.media('md')} {
    flex-direction: row;
    align-items: flex-end;
    gap: ${rem(24)};
    margin-top: auto;
  }
`;

const Bullet = styled.li`
  display: flex;
  align-items: center;
  gap: ${rem(16)};
  font-size: ${rem(18)};
  line-height: 1.4;
  color: ${DARK};
  &:first-child {
    max-width: ${rem(420)};
  }
`;

const BulletBadge = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${rem(52)};
  height: ${rem(52)};
  border-radius: ${rem(15)};
  background: rgba(0, 0, 0, 0.4);
`;

const BulletGlyph = styled.img`
  width: ${rem(28)};
  height: ${rem(28)};
`;

const SkvelkoGirlSlot = styled.div`
  display: none;
  ${({ theme }) => theme.media('md')} {
    display: block;
    position: relative;
    align-self: stretch;
    /* 25% of the whole banner (left column is 60%) */
    flex: 25 1 0;
    min-width: 0;
  }
  /* Tablet / small desktop (≤1024px): even 30/30 split with the bullets. */
  ${({ theme }) => theme.media('md', 1125)} {
    flex: 30 1 0;
  }
`;

const SkvelkoGirl = styled.img`
  display: none;
  ${({ theme }) => theme.media('md')} {
    display: block;
    position: absolute;
    width: 100%;
    height: auto;
    object-fit: contain;
    object-position: bottom;
    pointer-events: none;
    /* Sit flush against the card's bottom edge (cancel the card's 28px padding)
       and push right so the girl slightly overlaps the form column. The form
       (SkvelkoWhiteCard, z-index 1) stays on top, so she peeks out behind it. */
    bottom: -${rem(28)};
    right: -${rem(130)};
    transform: scale(1.3);
    transform-origin: right bottom;
  }
  /* Tablet / small desktop (≤1024px): even 30/30 split with the bullets. */
  ${({ theme }) => theme.media('md', 1125)} {
    right: -${rem(124)};
  }
`;

const SkvelkoFormCol = styled.div`
  display: flex;
  flex-direction: column;
  & > * {
    flex: 1 1 auto;
  }
  ${({ theme }) => theme.media('md')} {
    flex: 2 1 0;
    min-width: 0;
  }
`;

const SkvelkoWhiteCard = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: ${rem(20)};
  background: ${({ theme }) => theme.color.white};
  border-radius: ${rem(24)};
  box-shadow: 0 ${rem(6)} ${rem(60)} rgba(0, 0, 0, 0.06);
  padding: ${rem(24)};
  ${({ theme }) => theme.media('md')} {
    padding: ${rem(28)} ${rem(32)};
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(6)};
`;

const FieldLabel = styled.span`
  font-size: ${rem(16)};
  font-weight: 600;
  color: ${DARK};
`;

const FieldHelp = styled.span`
  font-size: ${rem(14)};
  line-height: 1.4;
  color: ${GREY};
`;

const RSelect: any = ReactSelectLib;

// Portal target so dropdown menus escape the card's `overflow: hidden`.
// Guarded for SSR (no `document` on the server).
const menuPortalTarget =
  typeof document !== 'undefined' ? document.body : undefined;

// A portalled + fixed menu computes its position from the control's
// bounding rect on each render, but doesn't listen to scroll — so on page
// scroll it stays put and appears detached. This hook forces the select to
// re-render on scroll while a menu is open, so react-select recomputes the
// portal position and the menu tracks its control. Returns `onMenuOpen` /
// `onMenuClose` handlers to spread onto every select sharing the finder.
const useMenuReposition = () => {
  const [, force] = React.useReducer((n: number) => n + 1, 0);
  const openCount = React.useRef(0);

  const onMenuOpen = React.useCallback(() => {
    openCount.current += 1;
    if (openCount.current === 1 && typeof window !== 'undefined') {
      // capture: true so scrolls on any ancestor scroller reposition too.
      window.addEventListener('scroll', force, true);
    }
  }, []);

  const onMenuClose = React.useCallback(() => {
    openCount.current = Math.max(0, openCount.current - 1);
    if (openCount.current === 0 && typeof window !== 'undefined') {
      window.removeEventListener('scroll', force, true);
    }
  }, []);

  React.useEffect(
    () => () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('scroll', force, true);
      }
    },
    [],
  );

  return { onMenuOpen, onMenuClose };
};

const NAV_KEYS = ['ArrowDown', 'ArrowUp', 'Home', 'End', 'PageUp', 'PageDown'];

const useKeyboardNav = () => {
  const [keyboardNav, setKeyboardNav] = React.useState(false);

  const onKeyDown = React.useCallback((e: any) => {
    if (NAV_KEYS.indexOf(e.key) !== -1) {
      setKeyboardNav(true);
    }
  }, []);

  const resetKeyboardNav = React.useCallback(() => setKeyboardNav(false), []);

  return { keyboardNav, onKeyDown, resetKeyboardNav };
};

const MOBILE_MEDIA = '(max-width: 991px)';
const MOBILE_SCROLL_GAP = 12;
const SELECT_PREFIX = 'skvelko-select';
const MENU_MARGIN_TOP = 8;
const MENU_LIST_PADDING = 8;
const OPTION_BORDER_LEFT = 2;

const useIsMobileViewport = () => {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }
    const mql = window.matchMedia(MOBILE_MEDIA);
    const onChange = (e: any) => setIsMobile(e.matches);
    setIsMobile(mql.matches);
    // addListener: still the only API in older mobile Safari.
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  return isMobile;
};

const fixedHeaderHeight = () => {
  const header = document.querySelector('header');
  if (!header || window.getComputedStyle(header).position !== 'fixed') {
    return 0;
  }
  return header.getBoundingClientRect().height;
};

const openMenuFitsBelow = (field: HTMLElement) => {
  const menu = document.querySelector('.' + SELECT_PREFIX + '__menu');
  if (!menu) {
    return false;
  }
  const needed =
    field.getBoundingClientRect().bottom +
    MENU_MARGIN_TOP +
    menu.getBoundingClientRect().height;
  return needed <= window.innerHeight - MOBILE_SCROLL_GAP;
};

const scrollFieldUnderHeader = (field: HTMLElement | null) => {
  if (!field || typeof window === 'undefined' || openMenuFitsBelow(field)) {
    return;
  }
  const delta =
    field.getBoundingClientRect().top - fixedHeaderHeight() - MOBILE_SCROLL_GAP;
  if (Math.abs(delta) < 2) {
    return;
  }
  const top = window.pageYOffset + delta;
  try {
    window.scrollTo({ top, behavior: 'smooth' });
  } catch (e) {
    // Older browsers: no options object overload.
    window.scrollTo(0, top);
  }
};

const selectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: '48px',
    backgroundColor: '#fff',
    borderColor: state.isDisabled ? '#d4d4d4' : DARK,
    borderWidth: '2px',
    borderRadius: '8px',
    boxShadow: 'none',
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    '&:hover': { borderColor: state.isDisabled ? '#d4d4d4' : DARK },
  }),
  valueContainer: (base: any) => ({ ...base, padding: '4px 14px' }),
  placeholder: (base: any) => ({ ...base, color: GREY, margin: 0 }),
  singleValue: (base: any) => ({ ...base, color: DARK }),
  input: (base: any) => ({ ...base, margin: 0, padding: 0 }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base: any, state: any) => ({
    ...base,
    color: state.isDisabled ? '#d4d4d4' : DARK,
  }),
  menu: (base: any) => ({
    ...base,
    zIndex: 20,
    marginTop: '8px',
    borderRadius: '8px',
    overflow: 'hidden',
    border: 'none',
    boxShadow:
      '0 4px 35px 0 rgba(0, 0, 0, 0.15), 0 4px 12px 0 rgba(0, 0, 0, 0.06)',
  }),

  // Above page content (the white card is z-index 1) but below the fixed
  // header (z-index 11 mobile / 45 desktop), so a menu tracking its control on
  // scroll slides *under* the header instead of painting over it.
  menuPortal: (base: any) => ({ ...base, zIndex: 10 }),
  menuList: (base: any) => ({
    ...base,
    padding: `${MENU_LIST_PADDING}px`,
    // Separators are pseudo-elements bleeding past this padding; never let that
    // bleed produce a horizontal scrollbar.
    overflowX: 'hidden',
    // The last row sits right above the footer's own border-top (or the menu
    // edge), so its separator would read as a double line.
    '> div:last-child::after': { display: 'none' },
    '::-webkit-scrollbar': { width: '8px' },
    '::-webkit-scrollbar-thumb': {
      background: 'rgba(0, 0, 0, 0.2)',
      borderRadius: '8px',
    },
    scrollbarWidth: 'thin',
  }),
  option: (base: any, state: any) => {
    const active =
      state.isSelected || (state.isFocused && state.selectProps.keyboardNav);
    return {
      ...base,
      cursor: 'pointer',
      fontSize: '16px',
      color: DARK,
      padding: '8px 12px',
      borderLeft: `${OPTION_BORDER_LEFT}px solid ${
        active ? DARK : 'transparent'
      }`,
      // The row (and its hover highlight) keeps the list's side padding, but
      // the separator runs the full width of the menu — so it bleeds back over
      // that padding as a pseudo-element instead of being a border on the row.
      position: 'relative',
      ...(state.selectProps.separators
        ? {
            '::after': {
              content: '""',
              position: 'absolute',
              left: `${-(MENU_LIST_PADDING + OPTION_BORDER_LEFT)}px`,
              right: `${-MENU_LIST_PADDING}px`,
              bottom: 0,
              height: '1px',
              background: '#e6e6e6',
            },
          }
        : {}),
      background: active ? '#fffae0' : 'transparent',
      fontWeight: active ? 600 : 400,
      ':hover': {
        borderLeftColor: DARK,
        background: '#fffae0',
      },
      ':active': { background: '#fffae0' },
    };
  },
};

const FinderHint = styled.p`
  margin: ${rem(4)} 0 0;
  display: flex;
  align-items: flex-start;
  gap: ${rem(10)};
  font-size: ${rem(16)};
  line-height: 1.5;
  color: ${DARK};

  b {
    font-weight: 700;
  }
  a {
    color: ${RED};
    text-decoration: underline;
  }
`;

const InfoIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    style={{ flex: '0 0 auto', marginTop: '2px' }}
  >
    <path
      d="M4 3h16a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-7l-4 4v-4H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z"
      fill="#e3173e"
    />
    <circle cx="12" cy="7" r="1.15" fill="#fff" />
    <rect x="10.95" y="9.4" width="2.1" height="5.2" rx="1.05" fill="#fff" />
  </svg>
);

interface FinderProps {
  cities: any[];
  schools: any[];
  sets: any[];
  selectedCityName: string;
  selectedSchoolId: number | null;
  selectedSkvelkoSetId: number | null;
  cityIsSelected: boolean;
  schoolIsSelected: boolean;
  skvelkoSetsIsEmpty: boolean;
  dispatch: any;
}

const filterOption = (option: any, inputValue: string) =>
  !inputValue ||
  (option.label &&
    normalizeString(option.label).includes(normalizeString(inputValue)));

const UNIVERSAL_CITY = 'Univerzálna sada';
const UNIVERSAL_SCHOOL_ID = 11602;

const CITY_FOOTER = {
  title: __('Ak obec nie je v zozname, môžete použiť univerzálnu sadu'),
  title2: __('Obec nie je v zozname, môžete použiť univerzálnu sadu'),
  option: __('Univerzálna sada'),
};
const SCHOOL_FOOTER = {
  title: __('Ak škola nie je v zozname, môžete použiť univerzálnu sadu'),
  title2: __('Škola nie je v zozname, môžete použiť univerzálnu sadu'),
  option: __('Univerzálna sada pre ZŠ'),
};

const SelectFooter = styled.div`
  border-top: ${rem(1)} solid #e6e6e6;
  padding: ${rem(10)} ${rem(14)};
  cursor: pointer;
  background: #fff;
  &:hover {
    background: #f7f7f7;
  }
`;

const SelectFooterTitle = styled.div`
  font-size: ${rem(13)};
  line-height: 1.4;
  color: ${GREY};
`;

const SelectFooterOption = styled.div`
  margin-top: ${rem(4)};
  font-size: ${rem(15)};
  font-weight: 600;
  color: ${RED};
`;

const makeFooterMenu =
  (
    footer: { title: string; title2: string; option: string },
    onFooterClick: () => void,
  ) =>
  (menuProps: any) => {
    const inputValue = menuProps.selectProps.inputValue || '';
    const options = menuProps.selectProps.options || [];
    const hasOptions = options.some((o: any) => filterOption(o, inputValue));
    const handleClick = (e: any) => {
      e.preventDefault();
      onFooterClick();
    };
    return (
      <components.Menu {...menuProps}>
        {hasOptions ? menuProps.children : null}
        <SelectFooter onMouseDown={handleClick} onTouchEnd={handleClick}>
          <SelectFooterTitle>
            {hasOptions ? footer.title : footer.title2}
          </SelectFooterTitle>
          <SelectFooterOption>{footer.option}</SelectFooterOption>
        </SelectFooter>
      </components.Menu>
    );
  };

const SkvelkoFinderBase = (props: FinderProps) => {
  const reposition = useMenuReposition();
  const { keyboardNav, onKeyDown, resetKeyboardNav } = useKeyboardNav();
  const isMobile = useIsMobileViewport();

  const cityFieldRef = React.useRef<HTMLDivElement>(null);
  const schoolFieldRef = React.useRef<HTMLDivElement>(null);
  const setFieldRef = React.useRef<HTMLDivElement>(null);

  const selectCommon = (fieldRef: React.RefObject<HTMLDivElement>) => ({
    keyboardNav,
    classNamePrefix: SELECT_PREFIX,
    onKeyDown,
    menuPlacement: 'bottom' as 'bottom',
    menuPosition: (isMobile ? 'absolute' : 'fixed') as 'absolute' | 'fixed',
    menuShouldScrollIntoView: !isMobile,
    onMenuOpen: () => {
      resetKeyboardNav();
      if (isMobile) {
        scrollFieldUnderHeader(fieldRef.current);
      }
      reposition.onMenuOpen();
    },
    onMenuClose: () => {
      resetKeyboardNav();
      reposition.onMenuClose();
    },
  });

  const onCityChange = (option: any) => {
    props.dispatch(setSkvelkoSetsBySchools([]));
    props.dispatch(setSkvelkoClassesBySchools([]));
    props.dispatch(setCityIsSelected(true));
    props.dispatch(setSchoolIsSelected(false));
    props.dispatch(setSelectedSchoolId(null));
    props.dispatch(setSelectedSkvelkoSetId(null));
    props.dispatch(setSelectedClassId(null));
    props.dispatch(setSelectedCityName(option.value));
    props.dispatch(loadSkvelkoSchoolsByCity(option.value));
  };

  const onSchoolChange = (option: any) => {
    props.dispatch(setSchoolIsSelected(true));
    props.dispatch(setSelectedSkvelkoSetId(null));
    props.dispatch(setSelectedClassId(null));
    props.dispatch(setSelectedSchoolId(option.value));
    props.dispatch(loadSkvelkoSetsBySchool(option.value));
    props.dispatch(loadSkvelkoClassesBySchool(option.value));
  };

  const onSetChange = (option: any) => {
    props.dispatch(setSelectedSkvelkoSetId(option.value));
    props.dispatch(setCurrentSet(null));
    props.dispatch(resetSkvelko());
    props.dispatch(push(`/sada/${option.value}`));
  };

  const cityOptions = props.cities.map((c: any) => ({
    value: c.value,
    label: c.value,
    selected: c.value === props.selectedCityName,
  }));

  const schoolOptions = props.schools.map((s: any) => ({
    value: s.value,
    label: s.name,
    selected: s.value === props.selectedSchoolId,
  }));

  const setOptions = props.sets.map((s: any) => ({
    value: s.value,
    label: s.name,
    selected: s.value === props.selectedSkvelkoSetId,
  }));

  const cityValue =
    cityOptions.find((o: any) => o.value === props.selectedCityName) ||
    (props.selectedCityName
      ? { value: props.selectedCityName, label: props.selectedCityName }
      : null);
  const schoolValue =
    schoolOptions.find((o: any) => o.value === props.selectedSchoolId) ||
    (props.selectedSchoolId === UNIVERSAL_SCHOOL_ID
      ? { value: UNIVERSAL_SCHOOL_ID, label: SCHOOL_FOOTER.option }
      : null);

  const cityRef = React.useRef<any>(null);
  const schoolRef = React.useRef<any>(null);

  const onCityFooterClick = React.useCallback(() => {
    props.dispatch(setSkvelkoSetsBySchools([]));
    props.dispatch(setSkvelkoClassesBySchools([]));
    props.dispatch(setCityIsSelected(true));
    props.dispatch(setSchoolIsSelected(false));
    props.dispatch(setSelectedSchoolId(null));
    props.dispatch(setSelectedSkvelkoSetId(null));
    props.dispatch(setSelectedClassId(null));
    props.dispatch(setSelectedCityName(UNIVERSAL_CITY));
    props.dispatch(loadSkvelkoSchoolsByCity(UNIVERSAL_CITY));
    if (cityRef.current) {
      cityRef.current.blur();
    }
  }, [props.dispatch]);

  const onSchoolFooterClick = React.useCallback(() => {
    props.dispatch(setSkvelkoSetsBySchools([]));
    props.dispatch(setSkvelkoClassesBySchools([]));
    props.dispatch(setCityIsSelected(true));
    props.dispatch(setSelectedCityName(UNIVERSAL_CITY));
    props.dispatch(loadSkvelkoSchoolsByCity(UNIVERSAL_CITY));
    props.dispatch(setSchoolIsSelected(true));
    props.dispatch(setSelectedSkvelkoSetId(null));
    props.dispatch(setSelectedClassId(null));
    props.dispatch(setSelectedSchoolId(UNIVERSAL_SCHOOL_ID));
    props.dispatch(loadSkvelkoSetsBySchool(UNIVERSAL_SCHOOL_ID));
    props.dispatch(loadSkvelkoClassesBySchool(UNIVERSAL_SCHOOL_ID));
    if (schoolRef.current) {
      schoolRef.current.blur();
    }
  }, [props.dispatch]);

  const cityComponents = React.useMemo(
    () => ({ Menu: makeFooterMenu(CITY_FOOTER, onCityFooterClick) }),
    [onCityFooterClick],
  );
  const schoolComponents = React.useMemo(
    () => ({ Menu: makeFooterMenu(SCHOOL_FOOTER, onSchoolFooterClick) }),
    [onSchoolFooterClick],
  );

  return (
    <SkvelkoWhiteCard>
      <Field ref={cityFieldRef}>
        <FieldLabel>{__('Zvoľte mesto')}</FieldLabel>
        <FieldHelp>
          {__('Ak obec nie je v zozname, môžete použiť univerzálnu sadu.')}
        </FieldHelp>
        <RSelect
          ref={cityRef}
          value={cityValue}
          options={cityOptions}
          placeholder={__('Vyberte mesto/obec')}
          onChange={onCityChange}
          filterOption={filterOption}
          styles={selectStyles}
          maxMenuHeight={220}
          menuPortalTarget={menuPortalTarget}
          {...selectCommon(cityFieldRef)}
          noOptionsMessage={() => __('Žiadne výsledky')}
          components={cityComponents}
        />
      </Field>

      <Field ref={schoolFieldRef}>
        <FieldLabel>{__('Zvoľte školu')}</FieldLabel>
        <RSelect
          key={props.selectedCityName}
          ref={schoolRef}
          value={schoolValue}
          options={schoolOptions}
          placeholder={__('Vyberte školu')}
          onChange={onSchoolChange}
          isDisabled={!props.cityIsSelected}
          separators={true}
          filterOption={filterOption}
          styles={selectStyles}
          maxMenuHeight={220}
          menuPortalTarget={menuPortalTarget}
          {...selectCommon(schoolFieldRef)}
          noOptionsMessage={() => __('Žiadne výsledky')}
          components={schoolComponents}
        />
      </Field>

      <Field ref={setFieldRef}>
        <FieldLabel>{__('Zvoľte ročník')}</FieldLabel>
        <RSelect
          options={setOptions}
          placeholder={__('Vyberte ročník')}
          onChange={onSetChange}
          isDisabled={!props.schoolIsSelected || props.skvelkoSetsIsEmpty}
          isSearchable={false}
          styles={selectStyles}
          maxMenuHeight={220}
          menuPortalTarget={menuPortalTarget}
          {...selectCommon(setFieldRef)}
        />
      </Field>

      <FinderHint>
        <InfoIcon />
        <span>
          <b>{__('Nenašiel si svoju školu alebo ročník?')}</b>{' '}
          {__('Ak si praješ, aby tvoja škola pribudla v našom zozname ')}
          <Link to={'/formular-pre-nezaradene-skoly'}>
            {__('kliknite sem')}
          </Link>
          .
        </span>
      </FinderHint>
    </SkvelkoWhiteCard>
  );
};

const mapFinderState = (state: any): Partial<FinderProps> => ({
  cities: prop(state.home, 'skvelkoCities', []),
  schools: prop(state.home, 'skvelkoSchools', []),
  sets: prop(state.home, 'skvelkoSets', []),
  selectedCityName: state.home.selectedCityName,
  selectedSchoolId: state.home.selectedSchoolId,
  selectedSkvelkoSetId: state.home.selectedSkvelkoSetId,
  cityIsSelected: state.home.cityIsSelected,
  schoolIsSelected: state.home.schoolIsSelected,
  skvelkoSetsIsEmpty: state.home.skvelkoSets
    ? !state.home.skvelkoSets.length
    : false,
});

const SkvelkoFinder = connect(mapFinderState)(SkvelkoFinderBase);

const BULLETS = [
  {
    icon: 'ico-teacher.svg',
    text: __('Presne podľa požiadaviek vášho triedneho učiteľa'),
  },
  { icon: 'ico-truck.svg', text: __(' Doprava zdarma nad 49 € po celej SR') },
  {
    icon: 'ico-clock.svg',
    text: __('Ušetrite čas a objednajte všetko na pár klikov'),
  },
];

interface SkvelkoBoxProps {
  hideGirl?: boolean;
}

export const SkvelkoBox = ({ hideGirl }: SkvelkoBoxProps) => {
  const [introOpen, setIntroOpen] = React.useState(false);

  return (
    <SkvelkoCard>
      <SkvelkoIntro>
        <SkvelkoBrandRow>
          <SkvelkoLogoWrap aria-hidden="true">
            <Skvelko size={190} />
          </SkvelkoLogoWrap>
          <SkvelkoHeading>
            <SrOnly>{__('Skvelko')} - </SrOnly>
            {__('Zoznamy pomôcok na nový školský rok')}
          </SkvelkoHeading>
        </SkvelkoBrandRow>
        <IntroToggle
          type="button"
          aria-expanded={introOpen}
          onClick={() => setIntroOpen(!introOpen)}
        >
          {__('Čo je to Skvelko?')}
          <Caret />
        </IntroToggle>
        <IntroCollapse height={introOpen ? 'auto' : 0}>
          <CollapseInner>
            <SkvelkoSubheading>
              {__(
                'Skvelko je miesto, kde nakúpite školské potreby, ktoré odporučil triedny učiteľ pre vášho školáka.',
              )}
            </SkvelkoSubheading>
            <SkvelkoText>
              {__(
                'Nájdete tu presné zloženie sady školských potrieb, na blížiaci sa školský rok 2026/2027. Všetko v jednej sade, na jednom mieste, objednané na pár klikov a doručené až k vám domov.',
              )}
            </SkvelkoText>
            <IntroBottom>
              <Bullets>
                {BULLETS.map((b) => (
                  <Bullet key={b.text}>
                    <BulletBadge>
                      <BulletGlyph
                        src={`${ASSET}/${b.icon}`}
                        alt=""
                        aria-hidden="true"
                        loading="lazy"
                        decoding="async"
                      />
                    </BulletBadge>
                    {b.text}
                  </Bullet>
                ))}
              </Bullets>
              {!hideGirl && (
                <SkvelkoGirlSlot>
                  <SkvelkoGirl
                    src="/images/skvelko-kid-2.webp"
                    width={362}
                    height={244}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    decoding="async"
                  />
                </SkvelkoGirlSlot>
              )}
            </IntroBottom>
          </CollapseInner>
        </IntroCollapse>
      </SkvelkoIntro>
      <SkvelkoFormCol>
        <SkvelkoFinder />
      </SkvelkoFormCol>
    </SkvelkoCard>
  );
};

export default SkvelkoBox;
